import { cn } from '@core/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef } from 'react';

import { Spinner } from '../Spinner/Spinner';

const buttonVariants = cva(
  'relative inline-flex items-center justify-center border border-solid outline-none transition-colors duration-150 ease-in-out focus-visible:ring-2 focus-visible:ring-offset-2',
  {
    variants: {
      theme: {
        'primary-fill':
          'border-transparent bg-Gray-black text-Gray-white hover:border-Gray-900 hover:bg-Gray-900 focus-visible:ring-Gray-black/50 active:border-Gray-800 active:bg-Gray-800',
        'primary-line':
          'border-Gray-black bg-Gray-white text-Gray-black hover:border-Gray-700 hover:text-Gray-700 focus-visible:ring-Gray-black/50 active:border-Gray-600 active:text-Gray-600',
        'primary-text':
          'border-transparent bg-inherit text-Gray-black hover:text-Gray-700 focus-visible:ring-Gray-black/50 active:text-Gray-600',
        'secondary-fill':
          'border-B-500 bg-B-500 text-Gray-white hover:border-B-600 hover:bg-B-600 focus-visible:ring-B-500/50 active:border-B-700 active:bg-B-700',
        'secondary-line':
          'border-B-500 bg-Gray-white text-B-500 hover:border-B-400 hover:text-B-600 focus-visible:ring-B-500/50 active:border-B-300 active:text-B-700',
        'secondary-text':
          'border-transparent bg-inherit text-B-500 hover:text-B-400 focus-visible:ring-B-500/50 active:text-B-300',
        'sub-fill':
          'border-Gray-200 bg-Gray-200 text-Gray-900 hover:border-Gray-100 hover:bg-Gray-100 focus-visible:ring-Gray-300/50 active:border-Gray-50 active:bg-Gray-50',
        'sub-line':
          'border-Gray-300 bg-Gray-white text-Gray-900 hover:border-Gray-400 hover:text-Gray-800 focus-visible:ring-Gray-300/50 active:border-Gray-400 active:text-Gray-700',
        'sub-text':
          'border-transparent bg-inherit text-Gray-800 hover:text-Gray-500 focus-visible:ring-Gray-300/50 active:text-Gray-400',
      },
      dimensions: {
        H56: 'h-56 gap-6 font-700 typo-body3',
        H48: 'h-48 gap-6 font-700 typo-body3',
        H40: 'h-40 gap-4 font-700 typo-body4',
        H32: 'h-32 gap-4 font-700 typo-caption1',
        H26: 'h-26 gap-4 font-600 typo-caption2',
      },
      roundness: {
        rectangle: 'rounded-xsmall',
        capsule: 'rounded-pill',
      },
    },
    compoundVariants: [
      {
        theme: [
          'primary-fill',
          'primary-line',
          'secondary-fill',
          'secondary-line',
          'sub-fill',
          'sub-line',
        ],
        dimensions: 'H56',
        className: 'px-24 py-16',
      },
      {
        theme: [
          'primary-fill',
          'primary-line',
          'secondary-fill',
          'secondary-line',
          'sub-fill',
          'sub-line',
        ],
        dimensions: 'H48',
        className: 'px-24 py-12',
      },
      {
        theme: [
          'primary-fill',
          'primary-line',
          'secondary-fill',
          'secondary-line',
          'sub-fill',
          'sub-line',
        ],
        dimensions: 'H40',
        className: 'px-16 py-10',
      },
      {
        theme: [
          'primary-fill',
          'primary-line',
          'secondary-fill',
          'secondary-line',
          'sub-fill',
          'sub-line',
        ],
        dimensions: 'H32',
        className: 'px-16 py-7',
      },
      {
        theme: [
          'primary-fill',
          'primary-line',
          'secondary-fill',
          'secondary-line',
          'sub-fill',
          'sub-line',
        ],
        dimensions: 'H26',
        className: 'px-10 py-5',
      },
      {
        theme: ['primary-text', 'secondary-text', 'sub-text'],
        dimensions: 'H56',
        className: 'p-16',
      },
      {
        theme: ['primary-text', 'secondary-text', 'sub-text'],
        dimensions: 'H48',
        className: 'px-16 py-12',
      },
      {
        theme: ['primary-text', 'secondary-text', 'sub-text'],
        dimensions: 'H40',
        className: 'px-8 py-10',
      },
      {
        theme: ['primary-text', 'secondary-text', 'sub-text'],
        dimensions: 'H32',
        className: 'px-8 py-7',
      },
      {
        theme: ['primary-text', 'secondary-text', 'sub-text'],
        dimensions: 'H26',
        className: 'px-4 py-5',
      },
    ],
    defaultVariants: {
      theme: 'primary-fill',
      dimensions: 'H56',
      roundness: 'rectangle',
    },
  },
);

//* hover·active까지 같이 지정해야 tailwind-merge가 theme의 hover 색을 덮어 비활성 버튼이 마우스에 반응하지 않는다. plain 클래스라 앱이 className으로 준 배경도 함께 덮는다 — loading에는 걸지 않는 이유다
const disabledVariants = cva('!cursor-not-allowed opacity-50', {
  variants: {
    theme: {
      'primary-fill':
        'border-Gray-100 bg-Gray-100 text-Gray-400 hover:border-Gray-100 hover:bg-Gray-100 active:border-Gray-100 active:bg-Gray-100',
      'primary-line':
        'border-Gray-400 bg-Gray-white text-Gray-400 hover:border-Gray-400 hover:text-Gray-400 active:border-Gray-400 active:text-Gray-400',
      'primary-text':
        'border-transparent bg-inherit text-Gray-black hover:text-Gray-black active:text-Gray-black',
      'secondary-fill':
        'border-Gray-100 bg-Gray-100 text-Gray-400 hover:border-Gray-100 hover:bg-Gray-100 active:border-Gray-100 active:bg-Gray-100',
      'secondary-line':
        'border-Gray-400 bg-Gray-white text-Gray-400 hover:border-Gray-400 hover:text-Gray-400 active:border-Gray-400 active:text-Gray-400',
      'secondary-text':
        'border-transparent bg-inherit text-B-500 hover:text-B-500 active:text-B-500',
      'sub-fill':
        'border-Gray-100 bg-Gray-100 text-Gray-400 hover:border-Gray-100 hover:bg-Gray-100 active:border-Gray-100 active:bg-Gray-100',
      'sub-line':
        'border-Gray-400 bg-Gray-white text-Gray-400 hover:border-Gray-400 hover:text-Gray-400 active:border-Gray-400 active:text-Gray-400',
      'sub-text':
        'border-transparent bg-inherit text-Gray-400 hover:text-Gray-400 active:text-Gray-400',
    },
  },
  defaultVariants: {
    theme: 'primary-fill',
  },
});

export interface ButtonProps
  extends
    ComponentPropsWithoutRef<'button'>,
    VariantProps<typeof buttonVariants> {
  children?: ReactNode;
  asChild?: boolean;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      theme,
      dimensions,
      roundness,
      type = 'button',
      children,
      asChild,
      loading,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const isDisabled = disabled || loading;

    const spinnerSize =
      dimensions === 'H40'
        ? 16
        : dimensions === 'H32' || dimensions === 'H26'
          ? 14
          : 20;

    return (
      <Comp
        ref={ref}
        className={cn(
          buttonVariants({
            dimensions,
            theme,
            roundness,
            className,
          }),
          loading && 'cursor-wait',
          !asChild && disabled && !loading && disabledVariants({ theme }),
        )}
        type={type}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <>
            <Spinner aria-hidden color="currentColor" size={spinnerSize} />
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);

Button.displayName = 'Button';
